import React, { useEffect, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { saleLotsMerge } from "actions";

import { Button } from "components/Form";
import { Column, Row } from "components/Layout";
import { CompactLotCard } from "components/LotCard/CompactLotCard";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";
import MessageBox from "components/MessageBox";

import CompactSaleLotCardList from "containers/WeighbridgeSaleView/CompactSaleLotCardList";

import {
  closeAllHashModals,
  openEditSaleLotModal,
  returnToLocation,
} from "lib/navigation";
import { getUnitPriceString } from "lib";

import { getReceiptPriceString } from "constants/pricingTypes";

import { getCombinedLotNumber } from "lib/saleLot";
import {
  getConflictingSoldSaleLots,
  getSoldSaleLotsMergedIntoUnsold,
  isMergeableSaleLotSold,
} from "lib/saleLot/mergeSoldSaleLots";

import { getBusinesses, getSaleLotsBySale } from "selectors";

interface MergeModalSaleLot {
  id: string;
  buyer_id?: string | null;
  buyer_way?: { id?: number | string | null; name?: string } | null;
  livestocksale_id: number;
  pricing_type_id: number;
  quantity: number;
  scannedCount?: number;
  total_mass_grams: number;
  total_price_cents: number;
  vendor?: { name?: string } | null;
  vendor_id: string;
}

type SubmitStep = "soldWarning" | "confirm" | null;

const getLotsLabel = (saleLots: MergeModalSaleLot[]): string =>
  `${saleLots.length === 1 ? "Lot" : "Lots"} ${saleLots
    .map(saleLot => `#${getCombinedLotNumber(saleLot)}`)
    .join(", ")}`;

interface SoldLotsWarningDialogProps {
  soldLots: MergeModalSaleLot[];
  hasConflictingSaleDetails: boolean;
  toLot: MergeModalSaleLot;
  closeSelf: () => void;
  onContinue: () => void;
}

const SoldLotsWarningDialog = ({
  soldLots,
  hasConflictingSaleDetails,
  toLot,
  closeSelf,
  onContinue,
}: SoldLotsWarningDialogProps) => {
  const businesses = useSelector(getBusinesses);
  const getSaleDetails = (saleLot: MergeModalSaleLot): string => {
    const buyerName = businesses[saleLot.buyer_id]?.name || "Unknown buyer";
    const buyerWayName = saleLot.buyer_way?.name;
    const price = getReceiptPriceString(
      getUnitPriceString(saleLot),
      saleLot.pricing_type_id,
    );
    return `${buyerName}${buyerWayName ? ` (${buyerWayName})` : ""} @ ${price}`;
  };
  const toLotLabel = getLotsLabel([toLot]);

  return (
    <Dialog open onClose={closeSelf} maxWidth="sm" fullWidth>
      <DialogTitle onClose={closeSelf}>Merge Sold Lots</DialogTitle>
      <DialogContent dividers>
        <div>
          {hasConflictingSaleDetails
            ? "These lots were sold to different buyers, buyer ways or prices:"
            : "These lots are sold and will be merged into an unsold lot:"}
        </div>
        <ul>
          {soldLots.map(saleLot => (
            <li key={saleLot.id}>
              {`${getLotsLabel([saleLot])}: ${getSaleDetails(saleLot)}`}
            </li>
          ))}
        </ul>
        <div>
          {isMergeableSaleLotSold(toLot)
            ? `Only ${toLotLabel}'s buyer, buyer way and price will be kept for all merged head.`
            : `${toLotLabel} is unsold, so the merged lot will have no buyer or price.`}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeSelf}>Cancel</Button>
        <Button data-tour="confirmMergeSoldLots" onClick={onContinue}>
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface ConfirmDialogProps {
  fromLots: MergeModalSaleLot[];
  toLot: MergeModalSaleLot;
  closeSelf: () => void;
  handleSubmit: (fromSaleLotIds: string[], toSaleLotId: string) => void;
}

const ConfirmDialog = ({
  fromLots,
  toLot,
  closeSelf,
  handleSubmit,
}: ConfirmDialogProps) => {
  const quantity = fromLots.reduce((sum, saleLot) => sum + saleLot.quantity, 0);
  const scannedCount = fromLots.reduce(
    (sum, saleLot) => sum + (saleLot.scannedCount || 0),
    0,
  );
  const fromLotsLabel = getLotsLabel(fromLots);
  return (
    <Dialog open onClose={closeSelf} maxWidth="sm" fullWidth>
      <DialogTitle id="form-dialog-title" onClose={closeSelf}>
        Merge Lots
      </DialogTitle>
      <DialogContent dividers>
        <div>
          {`Move ${quantity} head and ${scannedCount} ${
            scannedCount === 1 ? "scan" : "scans"
          } from ${fromLotsLabel} into ${getLotsLabel([toLot])}?`}
        </div>
        <div>{`${fromLotsLabel} will be deleted. This cannot be undone.`}</div>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeSelf}>Cancel</Button>
        <Button
          data-tour="confirmMerge"
          onClick={() =>
            handleSubmit(
              fromLots.map(saleLot => saleLot.id),
              toLot.id,
            )
          }
        >
          Merge
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface MergeSalelotModalProps {
  saleLotId: string;
  returnTo?: string;
  onClose?: () => void;
  onMerged?: (fromSaleLotIds: string[], toSaleLotId: string) => void;
}

export const MergeSalelotModal = ({
  saleLotId,
  returnTo,
  onClose,
  onMerged,
}: MergeSalelotModalProps) => {
  const closeSelf = () => {
    if (onClose) {
      onClose();
    } else if (returnTo) {
      returnToLocation(returnTo);
    } else {
      closeAllHashModals();
    }
  };

  const vendorLots: MergeModalSaleLot[] = useSelector(state => {
    const allLots: MergeModalSaleLot[] = getSaleLotsBySale(state);
    const referenceLot = allLots.find(({ id }) => id === saleLotId);
    if (!referenceLot) {
      return [];
    }
    return allLots.filter(
      saleLot =>
        saleLot.vendor_id === referenceLot.vendor_id &&
        saleLot.livestocksale_id === referenceLot.livestocksale_id,
    );
  });

  const [selectedLotIds, setSelectedLotIds] = useState<string[]>([]);
  const [isChoosingLotToKeep, setIsChoosingLotToKeep] = useState(false);
  const [toSaleLotId, setToSaleLotId] = useState<string | null>(null);
  const [submitStep, setSubmitStep] = useState<SubmitStep>(null);

  const dispatch = useDispatch();
  const hasSubmittedRef = useRef(false);
  const handleSubmit = (fromLotIds: string[], toLotId: string) => {
    hasSubmittedRef.current = true;
    dispatch(saleLotsMerge(fromLotIds, toLotId));
    if (onMerged) {
      onMerged(fromLotIds, toLotId);
    } else {
      openEditSaleLotModal(toLotId);
    }
  };

  const thisLot = vendorLots.find(saleLot => saleLot.id === saleLotId) || null;
  const otherLotIds = vendorLots
    .filter(saleLot => saleLot.id !== saleLotId)
    .map(saleLot => saleLot.id);
  // Lots merged away on another device drop out of the selection here.
  const selectedLots = vendorLots.filter(saleLot =>
    selectedLotIds.includes(saleLot.id),
  );
  const liveSelectedLotIds = selectedLots.map(saleLot => saleLot.id);

  const toggleSelectedLot = (id: string) =>
    setSelectedLotIds(ids =>
      ids.includes(id)
        ? ids.filter(selectedId => selectedId !== id)
        : [...ids, id],
    );
  const areAllSelected = selectedLots.length === otherLotIds.length;
  const toggleSelectAll = () =>
    setSelectedLotIds(areAllSelected ? [] : otherLotIds);

  const handleBack = () => {
    if (isChoosingLotToKeep) {
      setIsChoosingLotToKeep(false);
      setSubmitStep(null);
      setToSaleLotId(null);
    } else {
      closeSelf();
    }
  };
  const handleNext = () => setIsChoosingLotToKeep(true);

  // The lot can be deleted underneath us (e.g. merged on another device).
  useEffect(() => {
    if (!thisLot && !hasSubmittedRef.current) {
      closeSelf();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thisLot]);

  if (!thisLot) {
    return null;
  }

  const candidateLots = [thisLot, ...selectedLots];
  const toLot = candidateLots.find(saleLot => saleLot.id === toSaleLotId);
  const fromLots = candidateLots.filter(saleLot => saleLot.id !== toSaleLotId);
  const conflictingSoldLots = getConflictingSoldSaleLots(candidateLots);
  const soldLotsToWarnAbout =
    conflictingSoldLots.length > 0 || !toLot
      ? conflictingSoldLots
      : getSoldSaleLotsMergedIntoUnsold(fromLots, toLot);

  const handleStartSubmit = () =>
    setSubmitStep(soldLotsToWarnAbout.length > 0 ? "soldWarning" : "confirm");

  return (
    <Dialog
      data-tour="mergeLotsDialog"
      open
      onClose={closeSelf}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle onClose={closeSelf}>Merge Lots</DialogTitle>
      <DialogContent dividers>
        {otherLotIds.length === 0 ? (
          <MessageBox>
            No other lots found in this sale belonging to{" "}
            {thisLot.vendor?.name || "this vendor"}.
          </MessageBox>
        ) : isChoosingLotToKeep ? (
          <>
            <Column justifyCenter alignCenter full>
              <h3>Select which lot to keep.</h3>
              <div>
                Head, weight and scans will be merged. All other information,
                including breed and marks, will be retained from the lot
                selected below.
              </div>
            </Column>
            {candidateLots.map(saleLot => (
              <CompactLotCard
                key={saleLot.id}
                {...saleLot}
                onClick={() => setToSaleLotId(saleLot.id)}
                selected={toSaleLotId === saleLot.id}
              />
            ))}
            {submitStep === "soldWarning" && toLot && (
              <SoldLotsWarningDialog
                soldLots={soldLotsToWarnAbout}
                hasConflictingSaleDetails={conflictingSoldLots.length > 0}
                toLot={toLot}
                closeSelf={() => setSubmitStep(null)}
                onContinue={() => setSubmitStep("confirm")}
              />
            )}
            {submitStep === "confirm" && toLot && (
              <ConfirmDialog
                fromLots={fromLots}
                toLot={toLot}
                closeSelf={() => setSubmitStep(null)}
                handleSubmit={handleSubmit}
              />
            )}
          </>
        ) : (
          <>
            <Row justifyCenter alignCenter>
              <h3>Merge</h3>
            </Row>
            <CompactLotCard {...thisLot} selected />
            <Row justifyBetween alignCenter>
              <h3>With (tap to select)</h3>
              <Button data-tour="selectAllLots" onClick={toggleSelectAll}>
                {areAllSelected ? "Clear All" : "Select All"}
              </Button>
            </Row>
            <CompactSaleLotCardList
              orderedIds={otherLotIds}
              selectedIds={liveSelectedLotIds}
              onSelectSaleLot={toggleSelectedLot}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleBack}>Back</Button>
        {isChoosingLotToKeep ? (
          <Button
            data-tour="submit"
            onClick={handleStartSubmit}
            disabled={!toLot}
          >
            Submit
          </Button>
        ) : (
          <Button
            data-tour="next"
            onClick={handleNext}
            disabled={liveSelectedLotIds.length === 0}
          >
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
